import {isString} from 'lodash';
import {transparentize} from 'polished';
import React, {Children} from 'react';
import styled, {css} from 'styled-components';

import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {NodeTransform} from '../../../../../srcCommon/formatters/markdown/markdownFormatterTypes';
import {isValidMention, transformMentions} from '../../../../helpers/models/commentHelpers';

/*
 * Props.
 */

interface MentionSpanStyleProps {
  $color?: string;
  $isUserMention?: boolean;
}

/*
 * Style.
 */

const StyledMentionSpan = styled.span<MentionSpanStyleProps>`
  color: ${(p) => p.$color || p.theme.interactiveText};
  font-weight: ${fontWeights.semibold};
  ${(p) => maybeAddBackground(p.$isUserMention)}
`;

function maybeAddBackground(isUserMention?: boolean) {
  if (!isUserMention) {
    return '';
  }

  return css`
    background-color: ${(p) => transparentize(0.85, p.theme.palette.blue.shade40)};
    border-radius: 4px;
    padding: 1px 2px 2px;
    margin: 0 -1px;
  `;
}

/*
 * Transform.
 */

export function makeMarkdownTransformMentions(
  mentionableAliases: ReadonlySet<string>,
  color?: string,
  isUserMention?: boolean,
): NodeTransform {
  return (node) =>
    Children.map(node, (child) => {
      // We only process strings.
      if (!isString(child)) {
        return child;
      }

      // Replace the mentions by a custom component.
      return transformMentions(child, (key, mention) => {
        if (!isValidMention(mentionableAliases, mention, isUserMention)) {
          return mention;
        }

        return (
          <StyledMentionSpan $color={color} $isUserMention={isUserMention} key={key}>
            {mention}
          </StyledMentionSpan>
        );
      });
    });
}
